import { FieldOptionBase } from 'module/api/field/types';
import { flatMap, map, ofNullable, orElse } from 'module/helpers/core/maybe';
import { TranslationKey, useTranslation } from 'module/hooks/use-translation';
import {
  SingleSelectField,
  SingleUserField,
} from 'module/types/filter-field/field/filterable-field';
import { snakeCase } from 'lodash';
import { pipe } from 'ramda';
import { FC, ReactNode } from 'react';
import { getControlledSelectBasedEditor } from '../..//field-editors/components/controlled-select-based-editor';
import { SelectFilterOperationType } from '../../../../../types/filter-field/filter/select';
import { TriggerContent } from '../../field-editors/components/controlled-select-based-editor-trigger-content';
import {
  EditorComponents,
  FieldEditorSelectMode,
} from '../../field-editors/types';

interface Props {
  field: SingleSelectField | SingleUserField;
  filterType:
    | SelectFilterOperationType.Is
    | SelectFilterOperationType.IsNot
    | SelectFilterOperationType.IsAtLeast
    | SelectFilterOperationType.IsAtMost;
  value: number | null;
  options: FieldOptionBase[];
  components: EditorComponents;
  type: FieldEditorSelectMode;
  onChange: (next: number | null) => void;
}

const Editor = getControlledSelectBasedEditor<false, number>(
  'single-select-editor',
  'editors.single-select.select',
);

export const SingleSelectEditor: FC<React.PropsWithChildren<Props>> = ({
  field,
  filterType,
  value,
  components: { TriggerOption, ...selectComponents },
  options,
  onChange,
  type,
}) => {
  const title = useTranslation('task_list.filters.messages.editor_title', {
    field: field.fieldLabel,
    operation: useTranslation(
      `task_list.filters.${snakeCase(filterType)}` as TranslationKey,
    ),
  });
  const items = pipe(
    () => value,
    ofNullable,
    map((x) => options.find((o) => o.value === x) ?? null),
    flatMap(ofNullable),
    // TODO: fix right typing here
    map((x: FieldOptionBase<number>) => [
      <TriggerOption option={x} key={x.value} />,
    ]),
    orElse<ReactNode[]>([]),
  )();

  return (
    <Editor
      title={title}
      components={selectComponents}
      isMulti={false}
      options={options}
      value={options.find((u) => u.value === value)}
      onChange={(selected?: FieldOptionBase<number>) =>
        onChange(selected?.value ?? null)
      }
      triggerContent={<TriggerContent items={items} />}
      type={type}
    />
  );
};
